<script>
import { gsap, Power4 } from 'gsap';
import { mapState, mapActions } from 'vuex';
import {
    TextureLoader,
    SpriteMaterial,
    Sprite,
    Group,
    PlaneGeometry,
    Mesh,
    MeshBasicMaterial,
    AdditiveBlending
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
// import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
export default {
    props: ['model', 'side', 'hotspots'],
    data() {
        return {
            currentModel: null,
            hotspotObjects: [],
            hotspotGroup: null,
            hotspotRadius: 56,
            radial: null,
            spriteMaterial: null,
            radialMaterial: null
        };
    },
    computed: {
        ...mapState('spinner', [
            'sceneLeft',
            'sceneRight',
            'loadingManager',
            'envMap'
        ]),
        scene() {
            return this.side == 'left' ? this.sceneLeft : this.sceneRight;
        }
    },
    mounted() {
        Promise.all([
            this.loadHotspotTexture(),
            this.loadHotspotSelectedTexture(),
            this.loadRadialTexture()
        ]).then(() => {
            this.init();
        });
    },
    beforeDestroy() {
        this.cleanup();
    },
    methods: {
        ...mapActions('spinner', ['setHotspotTexture']),

        init() {
            if (this.model) {
                this.hotspotGroup = new Group();

                if (this.currentModel) {
                    this.currentModel.visible = false;
                }

                let gltfLoader = new GLTFLoader(this.loadingManager);
                // SCREW IE 11
                // let dracoLoader = new DRACOLoader();
                // dracoLoader.preload();
                // dracoLoader.setDecoderPath('/libs/draco/');
                // dracoLoader.setDecoderConfig({ type: 'js' });
                // gltfLoader.setDRACOLoader(dracoLoader);

                gltfLoader.load(this.model, gltf => {
                    let model = gltf.scene;

                    // Environment Map and base RoughnessMetallic
                    this.traverseMaterials(model, material => {
                        if (
                            material.isMeshStandardMaterial ||
                            material.isGLTFSpecularGlossinessMaterial
                        ) {
                            material.envMap = this.envMap;
                            material.envMapIntensity = 0.75;
                            material.needsUpdate = true;
                        }
                    });

                    this.hotspotObjects = [];
                    this.currentModel = model;
                    this.findHotspots(model);

                    model.rotation.set(0, 0, Math.PI * 0.035);
                    // model.updateMatrixWorld();

                    this.scene.add(model);
                    if (this.hotspots && this.hotspots instanceof Function) {
                        this.hotspots(this.hotspotObjects, this.side);
                    }

                    this.scene.add(this.hotspotGroup);

                    gsap.fromTo(
                        this.spriteMaterial,
                        { opacity: 0 },
                        {
                            opacity: 1,
                            duration: 0.5,
                            delay: 1,
                            ease: Power4.easeOut
                        }
                    );

                    if (this.side == 'right') {
                        this.scaleIn();
                    }
                });

                if (!this.radial) {
                    const planeGeo = new PlaneGeometry(8, 8, 1, 1);
                    planeGeo.rotateX(-Math.PI * 0.5);
                    this.radial = new Mesh(planeGeo, this.radialMaterial);
                    this.radial.position.set(0, -6, 0);
                    this.scene.add(this.radial);
                }
            }
        },

        traverseMaterials(object, callback) {
            object.traverse(node => {
                if (!node.isMesh) return;
                const materials = Array.isArray(node.material)
                    ? node.material
                    : [node.material];
                materials.forEach(callback);
            });
        },

        findHotspots(model) {
            model.traverse(node => {
                if (node.name.indexOf('hotspot') != -1) {
                    this.hotspotObjects.push(this.addHotspotSprite(node));
                }
            });
        },

        loadHotspotTexture() {
            return new Promise((resolve, reject) => {
                let textureLoader = new TextureLoader();
                textureLoader.load(
                    'images/textures/hotspot.png',
                    texture => {
                        this.setHotspotTexture({
                            type: 'off',
                            texture
                        });
                        this.spriteMaterial = new SpriteMaterial({
                            map: texture,
                            depthTest: false,
                            transparent: true,
                            depthWrite: false,
                            flatShading: true,
                            toneMapped: false
                        });
                        resolve();
                    },
                    null,
                    error => {
                        reject(error);
                    }
                );
            });
        },

        loadHotspotSelectedTexture() {
            return new Promise((resolve, reject) => {
                let textureLoader = new TextureLoader();
                textureLoader.load(
                    'images/textures/hotspot_selected.png',
                    texture => {
                        this.setHotspotTexture({
                            type: 'selected',
                            texture
                        });
                        resolve();
                    },
                    null,
                    error => {
                        reject(error);
                    }
                );
            });
        },

        addHotspotSprite(hotspot) {
            let sprite = new Sprite(this.spriteMaterial.clone());
            sprite.name = `sprite_${hotspot.name}`;
            sprite.position.set(
                hotspot.position.x,
                hotspot.position.y,
                hotspot.position.z
            );
            // sprite.position.normalize();
            // sprite.position.multiplyScalar(this.hotspotRadius);
            this.hotspotGroup.add(sprite);

            return sprite;
        },

        loadRadialTexture() {
            return new Promise((resolve, reject) => {
                let textureLoader = new TextureLoader();
                textureLoader.load(
                    'images/textures/radial.png',
                    texture => {
                        this.radialMaterial = new MeshBasicMaterial({
                            map: texture,
                            blending: AdditiveBlending,
                            opacity: 0.4
                        });
                        resolve();
                    },
                    null,
                    error => {
                        reject(error);
                    }
                );
            });
        },

        cleanup() {
            if (this.currentModel) {
                if (this.hotspots && this.hotspots instanceof Function) {
                    this.hotspots([], this.side, true);
                }
                this.scene.remove(this.hotspotGroup);
                this.scene.remove(this.currentModel);
            }
        },

        scaleIn() {
            gsap.fromTo(
                this.currentModel.scale,
                { x: 0, y: 0, z: 0 },
                { x: 1, y: 1, z: 1, duration: 0.5, ease: Power4.easeOut }
            );
        }
    },
    watch: {
        model() {
            if (this.radialMaterial && this.spriteMaterial) {
                this.cleanup();
                this.init();
            }
        }
    },
    render(h) {
        return h('div', this.$slots.default);
    }
};
</script>
